import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { HeaderConfigService } from '@os-components';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Author } from '../../models/author.model';
import {
    BlogLayout,
    BlogPost,
    RootObjectBlogLayout,
    RootObjectBlogPosts,
} from '../../models/blog-posts.model';
import { BlogLabels, RootObjectLabels } from '../../models/label.model';
import { AppConstants } from '../../shared/app.constants';

export const IS_PREVIEW_TOKEN = new InjectionToken<boolean>('IsPreview');

@Injectable({
    providedIn: 'root',
})
export class BlogService {
    private labelsSubject = new ReplaySubject<BlogLabels>(1);
    private blogSuffixSubject = new ReplaySubject<string>(1);
    private languageSubject = new ReplaySubject<string>(1);

    private labelsObj: BlogLabels = {} as BlogLabels;

    private baseCaasUrl: string;
    private blogSuffix?: string;

    private currentLanguage = '';

    private sort = '-date';

    private totalPostPages?: number;

    public constructor(
        private http: HttpClient,
        private configService: HeaderConfigService,
        @Inject(IS_PREVIEW_TOKEN) private isPreview: boolean
    ) {
        this.baseCaasUrl = environment.caas.baseUrl;

        this.configService.countryShort.subscribe((countryShort) => {
            this.blogSuffix = `Blog-${countryShort.toUpperCase()}`;
            this.blogSuffixSubject.next(this.blogSuffix);
        });

        // language change listener
        this.configService.language.subscribe((language) => {
            this.currentLanguage = language.toUpperCase();
            this.languageSubject.next(this.currentLanguage);
        });
    }

    public getTotalPostPages(): number {
        return this.totalPostPages ?? 0;
    }

    public loadLabels(): void {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogLabels();
        const params = this.getDefaultParams();
        this.http
            .get<RootObjectLabels>(url, { params })
            .pipe(
                map((response) => {
                    if (response._embedded) {
                        return response._embedded['rh:doc'][0].labels;
                    } else {
                        return null;
                    }
                })
            )
            .subscribe((response: BlogLabels | null) => {
                if (!response) {
                    return;
                }

                this.labelsObj = response;
                this.labelsSubject.next(this.labelsObj);
            });
    }

    /**
     * Get all posts from backend.
     */
    public getBlogContent(page: number, pagesize: number): Observable<BlogPost[]> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();
        let params = this.getDefaultParams()
            .append('page', page.toString())
            .append('count', 'true')
            .append('pagesize', pagesize.toString())
            .append('sort', this.sort);
        if (!this.isPreview) {
            params = params.append('filter', this.lowerThanDateFilter());
        }

        return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
            map((response) => {
                this.totalPostPages = response._total_pages;
                if (response._embedded) {
                    return response._embedded['rh:doc'];
                } else {
                    return [];
                }
            })
        );
    }

    public getBlogLayoutPosts(): Observable<BlogLayout[]> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogLayoutPosts();
        const params = this.getDefaultParams();

        return this.http.get<RootObjectBlogLayout>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'];
                } else {
                    return [];
                }
            })
        );
    }

    public getPostsByAuthor(id: string): Observable<BlogPost[]> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();
        let params = this.getDefaultParams()
            .append('page', '1')
            .append('count', 'true')
            .append('pagesize', '9')
            .append('sort', this.sort)
            .append('filter', '{ "author.author_id" : "' + id + '"}}');
        if (!this.isPreview) {
            params = params.append('filter', this.lowerThanDateFilter());
        }

        return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'];
                } else {
                    return [];
                }
            })
        );
    }

    public getPostsByTags(tags: string[]): Observable<BlogPost[]> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();
        const arr = [];
        for (const tag of tags) {
            arr.push(`{ 'tags': { '$in': ['${tag}', 'crud'] } }`);
        }
        let params = new HttpParams()
            .append('page', '1')
            .append('count', 'true')
            .append('pagesize', '15')
            .append('sort', this.sort)
            .append(
                'filter',
                `{'$and': [{'fs_language': '${this.currentLanguage}'}, {'$or': [${arr.toString()}] } ]}`
            );

        if (!this.isPreview) {
            params = params.append('filter', this.lowerThanDateFilter());
        }

        return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'];
                } else {
                    return [];
                }
            })
        );
    }

    public getPostById(id: string): Observable<BlogPost | null> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();
        const params = new HttpParams().append(
            'filter',
            `{'$and': [{'fs_language': '${this.currentLanguage}'}, {'page_id': '${id}'}] }`
        );

        return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'][0];
                } else {
                    return null;
                }
            })
        );
    }

    public getPostByPreviewId(previewId: string): Observable<BlogPost | null> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();

        // the previewId contains the selected language
        // therefore we set the selected language to the preview language to reflect this change
        const previewObj = JSON.parse(atob(previewId));
        const dataFsAttrs = JSON.parse(atob(previewObj['data-fs-attrs']));
        const previewLanguage = dataFsAttrs.languageAbbreviation;
        if (previewLanguage) {
            this.currentLanguage = previewLanguage;
        }

        const params = new HttpParams().append('filter', `{'node_preview_id': '${previewId}'}`);

        return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'][0];
                } else {
                    return null;
                }
            })
        );
    }

    public getAuthorByPreviewId(previewId: string): Observable<Author> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogAuthorsURL();
        const params = new HttpParams().append(
            'filter',
            `{'$and': [{'fs_language': '${this.currentLanguage}'}, {'node_preview_id': '${previewId}'}] }`
        );

        return this.http.get<any>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'][0];
                } else {
                    return null;
                }
            })
        );
    }

    public getPostByNodeId(nodeId: string): Observable<BlogPost | null> {
        return combineLatest([this.blogSuffixSubject, this.languageSubject]).pipe(
            switchMap(([suffix, language]) => {
                const url = this.baseCaasUrl + '/direct/' + suffix + AppConstants.blogPosts();
                const params = new HttpParams().append(
                    'filter',
                    `{'$and': [{'fs_language': '${language}'}, {'fs_node_id': ${nodeId}}] }`
                );

                return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
                    map((response) => {
                        if (response._embedded) {
                            return response._embedded['rh:doc'][0];
                        } else {
                            return null;
                        }
                    })
                );
            })
        );
    }

    public getPostBySeoUrl(seoUrl: string): Observable<BlogPost | null> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();
        const params = new HttpParams().set('filter', `{'url':{'$regex' : '.*${seoUrl}'}}`);

        return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'][0];
                } else {
                    return null;
                }
            })
        );
    }

    public getPostsById(ids: string[]): Observable<BlogPost[]> {
        if (ids.length) {
            const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();
            const arr = [];
            for (const id of ids) {
                arr.push(`{'page_id' : '${id}'}`);
            }
            let params = new HttpParams().append(
                'filter',
                `{'$and': [{'fs_language': '${this.currentLanguage}'}, {'$or': [${arr.toString()}] } ]}`
            );
            if (!this.isPreview) {
                params = params.append('filter', this.lowerThanDateFilter());
            }

            return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
                map((response) => {
                    if (response._embedded) {
                        return response._embedded['rh:doc'];
                    } else {
                        return [];
                    }
                })
            );
        } else {
            return of<BlogPost[]>([]);
        }
    }

    public getAuthorByNodeId(nodeId: string): Observable<Author> {
        const authorUrl = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogAuthorsURL();
        const params = new HttpParams().append(
            'filter',
            `{'$and': [{'fs_language': '${this.currentLanguage}'}, {'fs_node_id': ${nodeId}}] }`
        );

        return this.http.get<any>(authorUrl, { params }).pipe(
            map((response) => {
                const author: Author = response._embedded ? response._embedded['rh:doc'][0] : null;

                return author;
            })
        );
    }

    public getAuthorByName(firstname: string, surname: string): Observable<Author | null> {
        const authorUrl = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogAuthorsURL();
        const params = new HttpParams().append(
            'filter',
            `{'$and': [{'fs_language': '${this.currentLanguage}'}, {'firstname': '${decodeURIComponent(
                firstname
            )}'}, {'surname': '${decodeURIComponent(surname)}'}] }`
        );

        return this.http.get<any>(authorUrl, { params }).pipe(
            map((response) => {
                const author: Author = response._embedded ? response._embedded['rh:doc'][0] : null;

                return author ? author : null;
            })
        );
    }

    public getSearchResults(query: string): Observable<BlogPost[]> {
        const url = this.baseCaasUrl + '/direct/' + this.blogSuffix + AppConstants.blogPosts();
        let params = new HttpParams()
            .append('page', '1')
            .append('count', 'true')
            .append('pagesize', '15')
            .append('sort', this.sort)
            .append(
                'filter',
                `{'$and': [{'fs_language': '${this.currentLanguage}'}, {'$or': [{'headline': {'$regex': '.*${query}.*', $options: 'i'}},
                 {'content.text': {'$regex': '.*${query}.*', $options: 'i'}},
                 {'tags': {'$regex': '.*${query}.*', $options: 'i'}}] } ]}`
            );
        if (!this.isPreview) {
            params = params.append('filter', this.lowerThanDateFilter());
        }

        return this.http.get<RootObjectBlogPosts>(url, { params }).pipe(
            map((response) => {
                if (response._embedded) {
                    return response._embedded['rh:doc'];
                } else {
                    return [];
                }
            })
        );
    }

    public get labelsUpdated(): Observable<BlogLabels> {
        return this.labelsSubject.asObservable();
    }

    public get labels(): BlogLabels {
        return this.labelsObj;
    }

    private lowerThanDateFilter() {
        return `{'date': {'$lt': '${this.currentDate}'}}`;
    }

    /**
     * returns paramter with filter set to currently selected language
     */
    private getDefaultParams(): HttpParams {
        return new HttpParams().set('filter', `{'fs_language': '${this.currentLanguage}'}`);
    }

    private get currentDate() {
        return new Date().toISOString().replace('T', ' ').substr(0, 19);
    }
}
